import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRotate, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { useQueryClient } from '@tanstack/react-query'
import { useAlertHistoryQuery } from '../../hooks/admin/useAlertHistory'
import type { AlertHistoryQuery, AlertHistoryRow } from '../../services/admin/alertsApi'
import { cn } from '../../shared/utils/cn'

const PAGE_SIZE = 50

const SEVERITY_OPTIONS = ['', 'INFO', 'WARNING', 'ERROR', 'CRITICAL']

export default function AlertHistoryLogPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const page = parseInt(searchParams.get('page') ?? '0', 10) || 0
  const alertType = searchParams.get('alertType') ?? ''
  const severity = searchParams.get('severity') ?? ''
  const deviceIdRaw = searchParams.get('deviceId') ?? ''
  const includeTests = searchParams.get('includeTests') === 'true'

  const query: AlertHistoryQuery = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      includeTests,
      ...(alertType ? { alertType } : {}),
      ...(severity ? { severity } : {}),
      ...(deviceIdRaw && Number.isFinite(Number(deviceIdRaw)) ? { deviceId: Number(deviceIdRaw) } : {}),
    }),
    [page, includeTests, alertType, severity, deviceIdRaw]
  )

  const { data, isLoading, isFetching, isError, error } = useAlertHistoryQuery(query)

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(searchParams)
    if (v) next.set(k, v)
    else next.delete(k)
    if (k !== 'page') next.set('page', '0')
    setSearchParams(next)
  }

  const goToPage = (p: number) => setParam('page', String(p))

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'alerts', 'history'] })
  }

  const rows = data?.rows ?? []
  const total = data?.pagination.total ?? 0
  const totalPages = data?.pagination.totalPages ?? 0

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between border-b border-gray-200 pb-4 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alert Log</h1>
          <p className="mt-1 text-sm text-gray-600">
            History of every alert generated. Test rows are hidden by default.
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={isFetching}
          className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-60"
        >
          <FontAwesomeIcon
            icon={isFetching ? faSpinner : faRotate}
            className={cn('h-4 w-4', isFetching && 'animate-spin')}
          />
          Refresh
        </button>
      </div>

      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
            <input
              type="text"
              value={alertType}
              onChange={(e) => setParam('alertType', e.target.value)}
              placeholder="e.g. BATTERY_LOW"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Severity</label>
            <select
              value={severity}
              onChange={(e) => setParam('severity', e.target.value)}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {SEVERITY_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s || 'Any'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Device ID</label>
            <input
              type="number"
              value={deviceIdRaw}
              onChange={(e) => setParam('deviceId', e.target.value)}
              placeholder="any"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-end">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={includeTests}
                onChange={(e) => setParam('includeTests', e.target.checked ? 'true' : '')}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Show test alerts
            </label>
          </div>
        </div>
      </section>

      <section className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th>Time</Th>
                <Th>Device</Th>
                <Th>Type</Th>
                <Th>Severity</Th>
                <Th>Title</Th>
                <Th className="text-right">Sent</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {isLoading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
              ) : isError ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-red-600">
                  {error instanceof Error ? error.message : 'Failed to load alerts.'}
                </td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No alerts match these filters.</td></tr>
              ) : (
                rows.map((r) => <Row key={r.id} row={r} />)
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 text-sm">
          <span className="text-gray-600">
            {total === 0 ? '0 results' : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total.toLocaleString()}`}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goToPage(Math.max(0, page - 1))}
              disabled={page === 0 || isFetching}
              className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-gray-600">
              Page {page + 1}{totalPages > 0 ? ` / ${totalPages}` : ''}
            </span>
            <button
              type="button"
              onClick={() => goToPage(page + 1)}
              disabled={page + 1 >= totalPages || isFetching}
              className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={cn('px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600', className)}>
      {children}
    </th>
  )
}

function Row({ row }: { row: AlertHistoryRow }) {
  const created = new Date(row.created)
  return (
    <tr className={cn(row.isTest && 'bg-amber-50/40')}>
      <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
        {created.toLocaleString()}
      </td>
      <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
        <span className="font-mono text-xs">{row.sigfoxId}</span>
        <span className="text-gray-400 text-xs"> · #{row.deviceId}</span>
      </td>
      <td className="px-4 py-2 text-gray-700 whitespace-nowrap">
        <span className="font-mono text-xs">{row.alertType}</span>
      </td>
      <td className="px-4 py-2 whitespace-nowrap">
        <SeverityBadge value={row.severity} />
      </td>
      <td className="px-4 py-2 text-gray-900">
        <div className="flex items-center gap-2">
          <span className="truncate max-w-md" title={row.title}>{row.title}</span>
          {row.isTest && (
            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 border border-amber-300">
              Test
            </span>
          )}
          {row.isRecovery && (
            <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 border border-green-300">
              Recovery
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-2 text-right text-gray-700 tabular-nums">{row.notificationsSent}</td>
    </tr>
  )
}

function SeverityBadge({ value }: { value: string }) {
  const classes: Record<string, string> = {
    CRITICAL: 'bg-red-100 text-red-800 border-red-300',
    ERROR: 'bg-red-100 text-red-800 border-red-300',
    WARNING: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    INFO: 'bg-blue-100 text-blue-800 border-blue-300',
  }
  const cls = classes[value] || 'bg-gray-100 text-gray-700 border-gray-300'
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium', cls)}>
      {value}
    </span>
  )
}
